import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import {
  useGetHotel,
  useGetPackageByHotelId,
} from 'app/hooks/data/hotels/useHotel';
import { useGetPackageCover } from 'app/hooks/data/packages/usePackages';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import type { IHotelWithDetails } from 'app/typings/migrationTool/HotelWithDetails';

import type { MergeSelection } from '../../HotelMigration';
import { HotelServiceCard } from '../../_components/HotelServiceCard/HotelServiceCard';
import { LongArrow } from '../../_components/Icons/LongArrow';
import type { MergeActionChildService } from '../../_components/MergeAction/MergeAction';
import { MergeAction } from '../../_components/MergeAction/MergeAction';
import { getRoomWithBedsList, getSelectedParentItem } from '../../utils';

import './Reviews.scss';

type Selections = Record<
  'rooms' | 'experiences' | 'packages',
  Record<number, MergeSelection>
>;

type ReviewsProps = {
  hotels?: IHotelWithDetails[];
  parentHotelId: number;
  childHotel?: IHotelWithDetails;
  selections: Selections;
};

export const Reviews = ({
  hotels,
  parentHotelId,
  childHotel,
  selections,
}: ReviewsProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { data: bedCategories } = useBedCategories();

  const { data: hotel } = useGetHotel(parentHotelId);
  const { data: packagesList } = useGetPackageByHotelId(parentHotelId);
  const mainParentHotelPackage = packagesList?.packages[0];

  const { data: packageCover } = useGetPackageCover(
    mainParentHotelPackage?.id ?? null
  );

  const { data: childPackageCover } = useGetPackageCover(
    childHotel?.packages[0]?.id ?? null
  );

  if (!hotels || !childHotel) {
    return null;
  }

  const parentHotel = hotels.find((h) => h.hotel.id === parentHotelId);

  if (
    !parentHotel ||
    !hotel ||
    !mainParentHotelPackage ||
    !packageCover ||
    !childPackageCover
  ) {
    return null;
  }

  const formattedChildHotel = {
    ...childHotel,
    packages: childHotel.packages.map((pkg) => ({
      ...pkg,
      coverPicture: {
        ...childPackageCover.horizontalCoverPicture,
        path: childPackageCover.horizontalCoverPicture.pictureId,
      },
    })),
  };

  const renderHotelSection = () => {
    const childHotelPackageExists =
      childHotel.packages && childHotel.packages.length > 0;

    return (
      <div>
        <h4 className="review-title">Hotel</h4>
        <div className="merge-actions-list">
          <div className="merge-action">
            <HotelServiceCard
              title={childHotel.hotel.name}
              description={
                childHotelPackageExists
                  ? childHotel.packages[0].aliasForHotel
                  : ''
              }
              isChild
              redirectUrl={`/hotels/${childHotel.hotel.id}/edit`}
              isPublished={
                childHotelPackageExists
                  ? childHotel.packages[0].isPublished
                  : false
              }
              picture={
                childHotelPackageExists
                  ? {
                      ...childPackageCover?.horizontalCoverPicture,
                      path: childPackageCover?.horizontalCoverPicture.pictureId,
                    }
                  : undefined
              }
            />
            <LongArrow />
            <HotelServiceCard
              title={hotel.name}
              isChild
              redirectUrl={`/hotels/${parentHotel.hotel.id}/edit`}
              isPublished={mainParentHotelPackage.published}
              description={mainParentHotelPackage.aliasForHotel}
              picture={{
                ...packageCover.horizontalCoverPicture,
                path: packageCover.horizontalCoverPicture.pictureId,
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (
    title: string,
    childItems: MergeActionChildService[],
    entity: keyof Selections,
    entityLabel: 'Room' | 'Experience' | 'Package'
  ) => {
    const entitySelections = selections[entity];

    if (!childItems.length) {
      return null;
    }

    return (
      <div>
        <h4 className="review-title">{title}</h4>
        <div className="merge-actions-list">
          {childItems.map((item) => {
            const selection: MergeSelection | undefined =
              entitySelections?.[item.id];

            if (!selection) {
              return null;
            }

            return (
              <div key={item.id} className="merge-action-wrapper">
                <MergeAction
                  childService={item}
                  parentService={parentHotel}
                  isReview
                  selectAction={selection.action}
                  selectedParentItem={getSelectedParentItem(
                    selection,
                    item.id,
                    childItems,
                    entityLabel,
                    parentHotel,
                    currencyFormatter,
                    bedCategories
                  )}
                  entityLabel={entityLabel}
                  childHotel={childHotel.hotel.name}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="migration-layout">
      <div className="migration-header">
        <h1>Review all steps of the merge</h1>
        <p>
          If something seems wrong, you can go back to the previous steps and
          your data will be saved.
        </p>
      </div>
      <div className="migration-content">
        {renderHotelSection()}
        {renderSection(
          'Rooms',
          formattedChildHotel.rooms.map((room) =>
            getRoomWithBedsList(room, currencyFormatter, bedCategories)
          ),
          'rooms',
          'Room'
        )}
        {renderSection(
          'Experiences',
          formattedChildHotel.experiences,
          'experiences',
          'Experience'
        )}
        {renderSection(
          'Packages',
          formattedChildHotel.packages,
          'packages',
          'Package'
        )}
      </div>
    </div>
  );
};
